import React, { useState, useEffect } from 'react';
import { User, UserCheck, UserX, Edit2, Trash2, Power, PowerOff } from 'lucide-react';
import { UserDto } from '../../../services/api';
import { usersApi } from '../../../services/api';

interface UsersListProps {
  onEdit?: (userId: string) => void;
  onRefresh?: () => void;
}

export const UsersList: React.FC<UsersListProps> = ({ onEdit, onRefresh }) => {
  const [users, setUsers] = useState<UserDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await usersApi.getAll();
      setUsers(data);
      onRefresh?.();
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Error al cargar los usuarios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      return;
    }

    try {
      setDeletingId(id);
      await usersApi.delete(id);
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch (err: any) {
      console.error('Error deleting user:', err);
      alert('Error al eliminar el usuario');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (user: UserDto) => {
    try {
      setTogglingId(user.id);
      if (user.isActive) {
        await usersApi.deactivate(user.id);
      } else {
        await usersApi.activate(user.id);
      }
      // Refresh the list to get updated status
      await fetchUsers();
    } catch (err: any) {
      console.error('Error toggling user status:', err);
      alert(`Error al ${user.isActive ? 'desactivar' : 'activar'} el usuario`);
    } finally {
      setTogglingId(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mb-4"></div>
          <p className="text-slate-600">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <User className="mx-auto mb-4 text-red-500" size={48} />
          <p className="text-red-600 font-medium mb-2">Error al cargar los usuarios</p>
          <p className="text-slate-600 text-sm">{error}</p>
          <button
            onClick={fetchUsers}
            className="mt-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <User className="h-12 w-12 mb-3" />
            <p className="text-lg font-medium">No hay usuarios</p>
            <p className="text-sm mt-2">Crea tu primer usuario para comenzar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Usuario</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Email</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Nombre Completo</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Rol</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-slate-700">Estado</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Último Acceso</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User size={16} className="text-green-600" />
                        </div>
                        <span className="font-medium text-slate-900">{user.username || '-'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-600 text-sm">{user.email || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-600 text-sm">{user.fullName || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        {user.role || 'Sin rol'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                          user.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.isActive ? (
                          <>
                            <UserCheck size={14} />
                            Activo
                          </>
                        ) : (
                          <>
                            <UserX size={14} />
                            Inactivo
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-600 text-sm">{formatDate(user.lastLoginAt)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {onEdit && (
                          <button
                            onClick={() => onEdit(user.id)}
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-blue-600 hover:text-blue-700"
                            title="Editar"
                          >
                            <Edit2 size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleToggleActive(user)}
                          disabled={togglingId === user.id}
                          className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                            user.isActive
                              ? 'hover:bg-orange-50 text-orange-600 hover:text-orange-700'
                              : 'hover:bg-green-50 text-green-600 hover:text-green-700'
                          }`}
                          title={user.isActive ? 'Desactivar' : 'Activar'}
                        >
                          {user.isActive ? <PowerOff size={18} /> : <Power size={18} />}
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          disabled={deletingId === user.id}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600 hover:text-red-700 disabled:opacity-50"
                          title="Eliminar"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
